import {
  Timestamp,
  type DocumentData,
  type DocumentSnapshot,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import type { WebinarCategory } from '@domain/models/webinar'

function toOptionalDate(value: unknown): Date | undefined {
  if (value instanceof Timestamp) {
    return value.toDate()
  }
  if (value instanceof Date) {
    return value
  }
  return undefined
}

function toBoolean(value: unknown): boolean | undefined {
  if (typeof value === 'boolean') {
    return value
  }
  if (typeof value === 'string') {
    if (value.toLowerCase() === 'true') {
      return true
    }
    if (value.toLowerCase() === 'false') {
      return false
    }
  }
  return undefined
}

function stripNullishDeep<T>(value: T): T {
  if (Array.isArray(value)) {
    const cleaned = value
      .map((item) => stripNullishDeep(item))
      .filter((item) => item !== null && item !== undefined)
    return cleaned as T
  }

  if (!value || typeof value !== 'object') {
    return value
  }

  if (value instanceof Date || value instanceof Timestamp) {
    return value
  }

  const output: Record<string, unknown> = {}
  for (const [key, nestedValue] of Object.entries(value as Record<string, unknown>)) {
    const cleaned = stripNullishDeep(nestedValue)
    if (cleaned !== null && cleaned !== undefined) {
      output[key] = cleaned
    }
  }

  return output as T
}

export function fromFirestoreWebinarCategory(
  docSnap: DocumentSnapshot<DocumentData> | QueryDocumentSnapshot<DocumentData>,
): WebinarCategory {
  const data = (docSnap.data() ?? {}) as Record<string, unknown>
  const createdAt = toOptionalDate(data['created_at']) ?? toOptionalDate(data['createdAt'])
  const isActive = toBoolean(data['is_active']) ?? toBoolean(data['isActive']) ?? false
  return {
    uid: docSnap.id,
    name: typeof data['name'] === 'string' ? data['name'] : '',
    createdAt,
    isActive,
  }
}

export function toFirestoreWebinarCategoryPatch(
  input: Partial<WebinarCategory>,
): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    name: input.name,
    created_at: input.createdAt ? Timestamp.fromDate(input.createdAt) : undefined,
    is_active: input.isActive,
  }

  return stripNullishDeep(payload)
}
